import React from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, Calendar, Target, Sparkles, CheckCircle2 } from 'lucide-react';

const FutureYouPreview = ({ quizResponses }) => {
  if (!quizResponses) return null;

  // Parse weights from quiz responses
  const parseWeight = (weightStr) => {
    if (!weightStr) return 0;
    const num = parseInt(String(weightStr).replace(/[^0-9]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const currentWeight = parseWeight(quizResponses.current_weight);
  const goalWeight = parseWeight(quizResponses.goal_weight);
  const timeline = quizResponses.timeline?.toLowerCase() || '';

  // If we can't determine weights, don't show the component
  if (!currentWeight || !goalWeight || currentWeight <= goalWeight) return null;

  const totalWeightLoss = currentWeight - goalWeight;

  // Determine number of months based on timeline preference
  const getTimelineMonths = () => {
    if (timeline.includes('1-3') || timeline.includes('asap')) return 3;
    if (timeline.includes('3-6')) return 5;
    if (timeline.includes('6-12') || timeline.includes('6+')) return 8;
    if (timeline.includes('12+') || timeline.includes('year')) return 12;
    return 6; // Default
  };

  const totalMonths = getTimelineMonths();
  
  // Calculate milestone data with realistic weight loss curve
  // GLP-1 medications typically show faster initial loss that tapers
  const calculateMilestones = () => {
    const milestones = [];
    
    // Weight loss curve: faster at start, slower over time
    // Month 1: ~15% of total, Month 2: ~25% cumulative, Month 3: ~40%, etc.
    const progressCurve = [0.15, 0.28, 0.42, 0.55, 0.67, 0.77, 0.85, 0.91, 0.95, 0.97, 0.99, 1.0];
    
    const milestoneData = [
      {
        month: 1,
        title: 'Early Progress',
        description: 'Initial adaptation period with early metabolic changes',
        highlight: 'Appetite regulation begins'
      },
      {
        month: 2,
        title: 'Building Momentum',
        description: 'Steady reduction trend as your body adjusts',
        highlight: 'Energy levels may improve'
      },
      {
        month: 3,
        title: 'Visible Changes',
        description: 'Noticeable body composition improvements',
        highlight: 'Clothes may fit differently'
      },
      {
        month: 4,
        title: 'Sustained Progress',
        description: 'Continued metabolic support and steady progress',
        highlight: 'Healthy habits forming'
      },
      {
        month: 6,
        title: 'Milestone Achievement',
        description: 'Significant progress toward your goal',
        highlight: 'Major transformation visible'
      },
      {
        month: 8,
        title: 'Advanced Progress',
        description: 'Approaching goal with sustained momentum',
        highlight: 'Lifestyle changes solidified'
      },
      {
        month: 12,
        title: 'Goal Zone',
        description: 'Maintenance phase and long-term success',
        highlight: 'Sustainable results achieved'
      }
    ];

    // Select milestones based on timeline
    let selectedMonths = [1, 2, 3, 4];
    if (totalMonths >= 6) selectedMonths = [1, 2, 3, 4, 6];
    if (totalMonths >= 8) selectedMonths = [1, 2, 4, 6, 8];
    if (totalMonths >= 12) selectedMonths = [1, 3, 6, 9, 12];

    selectedMonths.forEach((monthNum, idx) => {
      const data = milestoneData.find(m => m.month === monthNum) || milestoneData[Math.min(idx, milestoneData.length - 1)];
      const progressPercent = progressCurve[Math.min(monthNum - 1, progressCurve.length - 1)];
      const projectedLoss = Math.round(totalWeightLoss * progressPercent);
      const projectedWeight = currentWeight - projectedLoss;
      
      milestones.push({
        month: monthNum,
        title: data.title,
        description: data.description,
        highlight: data.highlight,
        projectedWeight: Math.max(projectedWeight, goalWeight),
        weightLost: projectedLoss,
        progressPercent: Math.round(progressPercent * 100)
      });
    });

    return milestones;
  };

  const milestones = calculateMilestones();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35 }}
      className="backdrop-blur-xl bg-gradient-to-br from-slate-950/60 via-emerald-950/20 to-slate-950/60 border border-emerald-500/20 rounded-2xl p-8 shadow-[0_0_40px_rgba(16,185,129,0.15)]"
      data-testid="future-you-preview"
    >
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500/30 to-cyan-500/20 flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(16,185,129,0.3)]">
          <TrendingDown className="h-7 w-7 text-emerald-400" />
        </div>
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2 text-white">
            Future You Preview
          </h2>
          <p className="text-slate-400 text-sm sm:text-base">
            Your projected weight loss trajectory based on your profile
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-center">
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Starting</p>
          <p className="text-2xl font-bold text-white">{currentWeight}</p>
          <p className="text-xs text-slate-400">lbs</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
          <p className="text-xs text-emerald-400 uppercase tracking-wide mb-1">Goal</p>
          <p className="text-2xl font-bold text-emerald-400">{goalWeight}</p>
          <p className="text-xs text-emerald-400/70">lbs</p>
        </div>
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 text-center">
          <p className="text-xs text-cyan-400 uppercase tracking-wide mb-1">To Lose</p>
          <p className="text-2xl font-bold text-cyan-400">{totalWeightLoss}</p>
          <p className="text-xs text-cyan-400/70">lbs</p>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-[23px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-500/50 via-emerald-500/50 to-emerald-500/20"></div>

        {/* Milestones */}
        <div className="space-y-6">
          {milestones.map((milestone, idx) => (
            <motion.div
              key={milestone.month}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + idx * 0.1 }}
              className="relative pl-14"
            >
              {/* Timeline dot */}
              <div className={`absolute left-0 w-12 h-12 rounded-full flex items-center justify-center ${
                idx === milestones.length - 1 
                  ? 'bg-emerald-500/30 border-2 border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)]'
                  : 'bg-cyan-500/20 border-2 border-cyan-500/50'
              }`}>
                {idx === milestones.length - 1 ? (
                  <Target className="h-5 w-5 text-emerald-400" />
                ) : (
                  <span className="text-sm font-bold text-cyan-300">M{milestone.month}</span>
                )}
              </div>

              {/* Content card */}
              <div className={`bg-slate-900/40 border rounded-xl p-5 ${
                idx === milestones.length - 1 
                  ? 'border-emerald-500/30 shadow-[0_0_20px_rgba(16,185,129,0.2)]'
                  : 'border-slate-800 hover:border-cyan-500/30'
              } transition-all`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-cyan-500" />
                      Month {milestone.month}: {milestone.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Projected</p>
                      <p className={`text-xl font-bold ${
                        idx === milestones.length - 1 ? 'text-emerald-400' : 'text-cyan-400'
                      }`}>
                        {milestone.projectedWeight} lbs
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                      idx === milestones.length - 1 
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-cyan-500/20 text-cyan-400'
                    }`}>
                      -{milestone.weightLost} lbs
                    </div>
                  </div>
                </div>
                
                <p className="text-slate-400 text-sm mb-3">{milestone.description}</p>
                
                <div className="flex items-center gap-2">
                  <CheckCircle2 className={`h-4 w-4 ${
                    idx === milestones.length - 1 ? 'text-emerald-500' : 'text-cyan-500'
                  }`} />
                  <span className={`text-sm font-medium ${
                    idx === milestones.length - 1 ? 'text-emerald-400' : 'text-cyan-400'
                  }`}>
                    {milestone.highlight}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Progress</span>
                    <span>{milestone.progressPercent}%</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${milestone.progressPercent}%` }}
                      transition={{ delay: 0.6 + idx * 0.1, duration: 0.8, ease: "easeOut" }}
                      className={`h-full rounded-full ${
                        idx === milestones.length - 1 
                          ? 'bg-gradient-to-r from-emerald-500 to-green-400'
                          : 'bg-gradient-to-r from-cyan-500 to-cyan-400'
                      }`}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Visible Disclaimer */}
      <div className="mt-8 pt-6 border-t border-emerald-500/20">
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/5 border border-amber-500/30 rounded-xl p-5" data-testid="future-you-disclaimer">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-amber-300 font-semibold text-sm mb-2">Important Disclaimer</p>
              <p className="text-slate-300 text-sm leading-relaxed">
                Projected milestones are illustrative only. Individual results vary and treatment decisions require provider review.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FutureYouPreview;
